import React, { forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, Icon, iconProps, useHeadingLevel } from '@zambezi/sdk/common'
import { STATUSES, SIZES, ARROW } from './constants'

/**
 * Create a React Notification component
 *
 * @param {import('./interfaces').NotificationProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function notification (props, ref) {
  const {
    title,
    status,
    size,
    withBackground,
    withStatusBackground,
    withStatusBorder,
    withArrow,
    arrowPosition,
    arrowDirection,
    children,
    className,
    iconProps,
    ariaHidden: propsAriaHidden,
    ariaLabel: propsAriaLabel,
    iconTitle,
    isFocusable,
    ...rest
  } = props
  /** @type {import('react').Ref<Node>} */
  const elementRef = useRef()
  const headingLevel = useHeadingLevel(elementRef, 3)
  const HeadingTag = `h${headingLevel}`
  const newIconProps = { ariaHidden: propsAriaHidden, isFocusable: false, title: iconTitle, ariaLabel: propsAriaLabel, ...iconProps }

  return (
    <div
      className={classNames(
        {
          'zb-notification': true,
          'zb-notification-status-error': status === STATUSES.ERROR,
          'zb-notification-status-info': status === STATUSES.INFO,
          'zb-notification-status-warning': status === STATUSES.WARNING,
          'zb-notification-status-success': status === STATUSES.SUCCESS,
          'zb-notification-small': size === SIZES.SMALL,
          'zb-notification-medium': size === SIZES.MEDIUM,
          'zb-notification-large': size === SIZES.LARGE,
          'zb-notification-has-border': withStatusBorder,
          'zb-notification-has-background': withBackground,
          'zb-notification-has-status-background': withStatusBackground,
          'zb-notification-has-arrow': withArrow,
          'zb-notification-without-title': !title
        },
        withArrow ? `zb-notification-has-arrow-${arrowDirection}` : '',
        className
      )}
      ref={ref}
      {...rest}
    >
      <div className='zb-notification-inner' ref={elementRef}>
        {withArrow && (
          <span
            className='zb-notification-arrow'
            style={
              arrowDirection === 'top' || arrowDirection === 'bottom'
                ? { left: arrowPosition }
                : { top: arrowPosition }
            }
          />
        )}
        <span className='zb-notification-icon' tabIndex={isFocusable ? 0 : null}>
          {renderIcon(status, size, newIconProps)}
        </span>

        <section className='zb-notification-body'>
          {title && (
            <HeadingTag className='zb-notification-title'>{title}</HeadingTag>
          )}
          {children}
        </section>
      </div>
    </div>
  )
}

/**
 * @param {import('./interfaces').State} status
 * @param {import('./interfaces').Size} size
 * @param {import('../icons/interfaces').IconProps} iconProps
 * @returns {JSX.Element}
 */
function renderIcon (status, size, iconProps) {
  const warningError = status === STATUSES.WARNING
    ? 'warning'
    : 'error'
  const successConfirm = status === STATUSES.SUCCESS
    ? 'confirmation'
    : warningError
  const icon =
  status === STATUSES.INFO
    ? 'information'
    : successConfirm
  const newSize = size === SIZES.MEDIUM ? 'medium' : 'small'
  const iconSize =
    size === SIZES.LARGE ? 'large' : newSize
  const iconName = `core-notification-${icon}-${iconSize}`

  return <Icon name={iconName} iconProps={iconProps} />
}

const Notification = forwardRef(notification)
Notification.displayName = 'Notification'

Notification.propTypes = {
  status: PropTypes.oneOf([
    STATUSES.ERROR,
    STATUSES.INFO,
    STATUSES.WARNING,
    STATUSES.SUCCESS
  ]),
  size: PropTypes.oneOf([SIZES.LARGE, SIZES.MEDIUM, SIZES.SMALL]),
  withBackground: PropTypes.bool,
  withStatusBackground: PropTypes.bool,
  withStatusBorder: PropTypes.bool,
  withArrow: PropTypes.bool,
  arrowDirection: PropTypes.oneOf([
    ARROW.TOP,
    ARROW.RIGHT,
    ARROW.BOTTOM,
    ARROW.LEFT
  ]),
  arrowPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconProps,
  ariaHidden: PropTypes.bool,
  ariaLabel: PropTypes.string,
  iconTitle: PropTypes.string,
  isFocusable: PropTypes.bool
}

Notification.defaultProps = {
  status: STATUSES.INFO,
  size: SIZES.MEDIUM,
  withBackground: true,
  withStatusBackground: true,
  withStatusBorder: true,
  withArrow: false,
  arrowDirection: ARROW.TOP,
  arrowPosition: 0,
  ariaHidden: false,
  isFocusable: false
}

export default Notification
